/**
 * BenchDrawer — sidebar list of bench players.
 *
 * Click → calls onPromote(player) (move to floor).
 * Drag-drop is wired by the parent screen (it owns the react-dnd setup
 * for both bench and floor lists).
 *
 * The drawer can be collapsed to save space — collapsed mode shows just a
 * vertical strip of avatars.
 */

import React from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip, ButtonBase } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';

export default function BenchDrawer({
  players = [],
  onPromote,
  collapsed = false,
  onToggleCollapse,
  renderItem,
  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  return (
    <Box
      sx={{
        width: collapsed ? 64 : 220,
        backgroundColor: theme.surfaces.elevated,
        borderRight: `1px solid ${theme.surfaces.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: `width ${theme.motion.base}`,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: 1.2,
          py: 1,
          borderBottom: `1px solid ${theme.surfaces.divider}`,
        }}
      >
        {!collapsed && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: theme.text.secondary,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {tt('bench', 'Bench')} · {players.length}
          </Typography>
        )}
        {onToggleCollapse && (
          <IconButton
            size="small"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'expand' : 'collapse'}
            sx={{ color: theme.text.secondary, width: 28, height: 28 }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: collapsed ? 0.6 : 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.6,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: withAlpha(theme.text.muted, 0.3),
            borderRadius: 3,
          },
        }}
      >
        {players.length === 0 && (
          <Typography sx={{ fontSize: 11, color: theme.text.muted, fontStyle: 'italic', textAlign: 'center', mt: 2 }}>
            {tt('emptyBench', 'No bench players')}
          </Typography>
        )}
        {players.map((player) => (
          typeof renderItem === 'function' ? (
            <React.Fragment key={player.id}>{renderItem(player, { collapsed })}</React.Fragment>
          ) : (
          <ButtonBase
            key={player.id}
            onClick={() => typeof onPromote === 'function' && onPromote(player)}
            aria-label={`promote ${player.nom}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 1,
              px: collapsed ? 0 : 0.8,
              py: 0.6,
              borderRadius: theme.radius.md,
              backgroundColor: theme.surfaces.floating,
              border: `1px solid ${theme.surfaces.border}`,
              justifyContent: collapsed ? 'center' : 'flex-start',
              cursor: 'grab',
              transition: `border-color ${theme.motion.fast}, background-color ${theme.motion.fast}`,
              '&:hover': {
                borderColor: theme.accentBorder,
                backgroundColor: theme.accentSofter,
              },
            }}
          >
            <Tooltip title={collapsed ? player.nom : ''}>
              <Avatar
                src={player.avatarUrl}
                sx={{
                  width: collapsed ? 36 : 32,
                  height: collapsed ? 36 : 32,
                  border: `1px solid ${theme.surfaces.border}`,
                  backgroundColor: theme.surfaces.elevated,
                  fontSize: 12,
                }}
              >
                {player.nom?.charAt(0).toUpperCase() || '?'}
              </Avatar>
            </Tooltip>
            {!collapsed && (
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                {player.dorsal && (
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: theme.accent, letterSpacing: '0.04em' }}>
                    #{player.dorsal}
                  </Typography>
                )}
                <Typography
                  noWrap
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: theme.text.primary,
                  }}
                >
                  {player.nom}
                </Typography>
              </Box>
            )}
          </ButtonBase>
          )
        ))}
      </Box>
    </Box>
  );
}
